/**
 * EmployerCompare — visual side-by-side comparison of ≤4 firms'
 * curated salary bands. Reads `?slugs=firm1,firm2,firm3` from the
 * URL and hits `/api/employers/compare`.
 *
 * Renders one column per firm with the flagship-role first-year
 * comp as a horizontal bar (scaled to the largest across the set)
 * so a family can see at a glance which firm actually pays more.
 * Below the bar chart, a stacked cards view repeats the full band
 * table for each location so the compare is also drill-able.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowLeft, TrendingUp, ShieldCheck, GraduationCap, Search, X, Plus } from "lucide-react";

// Format the source `as_of` (typically "2025-Q4" or ISO date) into the
// vintage families expect to see on a benchmark chip. Mirrors the
// formatter on EmployerSalaryBands so the same "Verified Jul 2026"
// signal appears everywhere firm pay data is shown.
function vintageLabelFor(asOf) {
  if (!asOf) return null;
  const s = String(asOf).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const dt = new Date(s + "T00:00:00Z");
    return dt.toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
  }
  if (/^\d{4}-\d{2}$/.test(s)) {
    const [y, m] = s.split("-");
    const dt = new Date(Date.UTC(parseInt(y, 10), parseInt(m, 10) - 1, 1));
    return dt.toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
  }
  const qMatch = s.match(/^(\d{4})-Q([1-4])$/i);
  if (qMatch) return `Q${qMatch[2]} ${qMatch[1]}`;
  return s;
}

export default function EmployerCompare() {
  const [params, setParams] = useSearchParams();
  const slugs = (params.get("slugs") || "").split(",").filter(Boolean);
  const { formatFrom } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-firm feeder-uni overlap ("which unis actually get me to Google?").
  // Keyed by slug → { firm_name, feeder_unis: [...], total_matches }
  // Loaded independently of the compare payload so a single 4xx on a
  // feeder call doesn't sink the whole compare view.
  const [feeders, setFeeders] = useState({});

  useEffect(() => {
    if (!slugs.length) {
      setLoading(false);
      return;
    }
    api
      .get(`/employers/compare?slugs=${slugs.join(",")}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ firms: [], count: 0 }))
      .finally(() => setLoading(false));
    // Fire-and-forget the feeder-unis fetches in parallel; each firm
    // populates its own slot in the feeders map as its response lands.
    Promise.all(
      slugs.map((s) =>
        api
          .get(`/employers/${encodeURIComponent(s)}/feeder-unis?limit=6`)
          .then((r) => [s, r.data])
          .catch(() => [s, null])
      )
    ).then((pairs) => {
      const map = {};
      for (const [s, d] of pairs) if (d) map[s] = d;
      setFeeders(map);
    });
    // eslint-disable-next-line
  }, [params.get("slugs")]);

  // Flatten every flagship row to a comparable USD equivalent so we
  // can scale the bar chart correctly across firms with different
  // native currencies.
  const compareRows = useMemo(() => {
    if (!data?.firms) return [];
    return data.firms
      .filter((f) => f.available)
      .flatMap((f) =>
        (f.flagship_by_location || []).map((fbl) => ({
          firm_name: f.firm_name,
          slug: f.slug,
          sector: f.sector,
          location_label: fbl.location_label,
          currency: fbl.currency,
          role: fbl.role,
          base_annual: fbl.base_annual,
          total_max_annual: fbl.total_max_annual,
        }))
      );
  }, [data]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <Link
        to="/employers"
        className="text-sm text-stone-500 hover:text-[#4C1D95] inline-flex items-center gap-1.5"
        data-testid="compare-back-employers"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to employers
      </Link>

      <div className="mt-4">
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <TrendingUp className="h-3 w-3" /> Firm compare
        </div>
        <h1
          className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight"
          data-testid="compare-heading"
        >
          Where can your hard earned degree earn you more?
        </h1>
        <p className="mt-3 text-stone-600 max-w-2xl leading-relaxed">
          Side-by-side flagship-role pay across up to four employers
          from our curated Q4 2025 catalog. Bars are converted to your
          currency for a fair compare; native amount stays in
          parentheses.
        </p>
      </div>

      {loading && (
        <div className="mt-8 p-4 text-stone-500 text-sm">Loading…</div>
      )}

      <FirmPicker slugs={slugs} setParams={setParams} data={data} />

      {!loading && data && !compareRows.length && slugs.length > 0 && (
        <EmptyState message="None of those firms are in our curated catalog yet." />
      )}

      {compareRows.length > 0 && (
        <CompareBarChart rows={compareRows} formatFrom={formatFrom} />
      )}

      {slugs.length > 0 && (
        <FeederOverlapSection slugs={slugs} feeders={feeders} />
      )}

      {data?.firms && (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {data.firms.map((f) => (
            <FirmMiniCard key={f.slug} firm={f} formatFrom={formatFrom} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div
      data-testid="compare-empty-state"
      className="mt-8 rounded-md border border-dashed border-stone-300 bg-stone-50/60 p-6 text-sm text-stone-600 text-center"
    >
      {message}
    </div>
  );
}

/**
 * FirmPicker — search + click UI so families don't need to hand-craft
 * the ?slugs URL. Backed by /api/employers/curated?q= for fuzzy match
 * on firm_name and aliases. Selected firms show as removable chips at
 * the top; up to 4 total.
 */
function FirmPicker({ slugs, setParams, data }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/employers/curated?q=${encodeURIComponent(q)}`)
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data.items || r.data.firms || r.data.rows || []).slice(0, 8);
        setSuggestions(rows);
      })
      .catch(() => { if (!cancelled) setSuggestions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  const addSlug = (slug) => {
    if (!slug || slugs.includes(slug) || slugs.length >= 4) return;
    const next = [...slugs, slug];
    setParams({ slugs: next.join(",") });
    setQuery("");
    setSuggestions([]);
  };
  const removeSlug = (slug) => {
    const next = slugs.filter((s) => s !== slug);
    if (next.length) setParams({ slugs: next.join(",") });
    else setParams({});
  };

  // Resolve slug → display name from the compare data payload (if
  // loaded) so removable chips show "Goldman Sachs" not "goldman-sachs".
  const nameFor = (slug) => {
    const f = data?.firms?.find((x) => x.slug === slug);
    return f?.firm_name || slug;
  };

  const atCapacity = slugs.length >= 4;

  return (
    <div className="mt-8" data-testid="firm-picker">
      <div className="rounded-lg border border-stone-200 bg-white p-4 sm:p-5">
        <div className="flex items-baseline justify-between gap-3">
          <div className="label-uppercase text-[#4C1D95] font-semibold">
            Pick up to four employers to compare
          </div>
          <div className="text-[11px] font-mono text-stone-400">
            {slugs.length} / 4 selected
          </div>
        </div>

        {slugs.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="firm-picker-chips">
            {slugs.map((s) => (
              <span
                key={s}
                data-testid={`firm-picker-chip-${s}`}
                className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-[#4C1D95] text-white text-sm"
              >
                {nameFor(s)}
                <button
                  onClick={() => removeSlug(s)}
                  data-testid={`firm-picker-remove-${s}`}
                  className="rounded-full p-0.5 hover:bg-white/20"
                  aria-label={`Remove ${nameFor(s)}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {!atCapacity && (
          <div className="mt-3 relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 200)}
                placeholder={
                  slugs.length === 0
                    ? "Search employers… try McKinsey, Jane Street, Goldman Sachs"
                    : "Add another employer to compare…"
                }
                data-testid="firm-picker-input"
                className="w-full pl-10 pr-3 py-2.5 rounded-md border border-stone-300 bg-stone-50 focus:bg-white focus:border-[#4C1D95] outline-none text-sm"
              />
            </div>
            {focused && suggestions.length > 0 && (
              <div
                data-testid="firm-picker-suggestions"
                className="absolute z-20 left-0 right-0 mt-1 rounded-md border border-stone-200 bg-white shadow-lg max-h-80 overflow-y-auto"
              >
                {suggestions.map((s) => {
                  const already = slugs.includes(s.slug);
                  return (
                    <button
                      key={s.slug}
                      onMouseDown={(e) => { e.preventDefault(); if (!already) addSlug(s.slug); }}
                      disabled={already}
                      data-testid={`firm-picker-suggestion-${s.slug}`}
                      className={
                        "w-full text-left px-4 py-2.5 flex items-baseline justify-between gap-3 transition-colors " +
                        (already
                          ? "bg-stone-50 text-stone-400 cursor-not-allowed"
                          : "hover:bg-[#F5F0FF] text-stone-800")
                      }
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{s.firm_name}</div>
                        <div className="text-[11px] font-mono text-stone-400 uppercase tracking-wider">
                          {s.sector || "other"}
                        </div>
                      </div>
                      {already ? (
                        <span className="text-[10px] font-mono text-stone-400">Added</span>
                      ) : (
                        <Plus className="h-4 w-4 text-[#4C1D95] shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {focused && query.trim().length >= 2 && !loading && suggestions.length === 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 rounded-md border border-stone-200 bg-white px-4 py-3 text-sm text-stone-500 shadow-lg">
                No employers match “{query}” in our curated catalog yet.
              </div>
            )}
          </div>
        )}

        {atCapacity && (
          <div className="mt-3 text-[12px] text-stone-500 flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
            You've hit the four-firm limit. Remove one to swap in another.
          </div>
        )}

        {slugs.length === 0 && (
          <div className="mt-3 text-[12px] text-stone-500">
            Popular starters:
            {" "}
            {["mckinsey", "jane-street", "goldman-sachs", "google"].map((s, i) => (
              <button
                key={s}
                onClick={() => addSlug(s)}
                data-testid={`firm-picker-quickadd-${s}`}
                className="ml-2 underline decoration-dotted underline-offset-2 hover:text-[#4C1D95]"
              >
                {s.replace(/-/g, " ")}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CompareBarChart({ rows, formatFrom }) {
  // Scale bars against the highest total_max (or base) across the set.
  const maxUsd = Math.max(
    ...rows.map((r) => {
      // Rough USD conversion for scaling only — real display uses
      // formatFrom which reads the shared exchange rates from context.
      const usdEquiv = (r.total_max_annual || r.base_annual) *
        (r.currency === "USD" ? 1 :
         r.currency === "GBP" ? 1.27 :
         r.currency === "EUR" ? 1.08 : 1);
      return usdEquiv;
    })
  );

  return (
    <div
      data-testid="compare-bar-chart"
      className="mt-8 rounded-lg border border-emerald-200 bg-emerald-50/30 p-5"
    >
      <div className="label-uppercase text-emerald-900 mb-4 font-semibold">
        Flagship graduate role · pay bar
      </div>
      <div className="space-y-3">
        {rows.map((r, i) => {
          const usdBase = (r.base_annual) *
            (r.currency === "USD" ? 1 : r.currency === "GBP" ? 1.27 : r.currency === "EUR" ? 1.08 : 1);
          const usdMax = (r.total_max_annual || r.base_annual) *
            (r.currency === "USD" ? 1 : r.currency === "GBP" ? 1.27 : r.currency === "EUR" ? 1.08 : 1);
          const basePct = Math.max(4, (usdBase / maxUsd) * 100);
          const maxPct = Math.max(basePct, (usdMax / maxUsd) * 100);
          return (
            <div key={i} data-testid={`compare-row-${i}`}>
              <div className="flex items-baseline justify-between text-[12px] mb-1 gap-4">
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-stone-800">{r.firm_name}</span>{" "}
                  <span className="text-stone-500">· {r.location_label}</span>
                  <span className="text-stone-400 text-[11px] ml-1">— {r.role}</span>
                </div>
                <div className="font-mono text-emerald-900 text-[12px] whitespace-nowrap">
                  {formatFrom(r.base_annual, r.currency)}
                  {r.total_max_annual != null && (
                    <>
                      {" → "}
                      <span className="font-semibold">
                        {formatFrom(r.total_max_annual, r.currency)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="relative h-5 rounded-full bg-white border border-emerald-200 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-emerald-200/70"
                  style={{ width: `${maxPct}%` }}
                  title="Total comp"
                />
                <div
                  className="absolute inset-y-0 left-0 bg-emerald-600"
                  style={{ width: `${basePct}%` }}
                  title="Base"
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-4 text-[11px] text-stone-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-emerald-600" /> base
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-emerald-200" /> total comp max
        </span>
      </div>
    </div>
  );
}

function FirmMiniCard({ firm, formatFrom }) {
  if (!firm.available) {
    return (
      <div
        data-testid={`compare-firm-${firm.slug}-unavailable`}
        className="rounded-md border border-dashed border-stone-300 bg-stone-50/60 p-4 text-sm text-stone-500"
      >
        <div className="font-medium text-stone-700">{firm.slug}</div>
        <div className="mt-1 text-[12px]">
          {firm.message || "Not in the curated catalog yet."}
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid={`compare-firm-${firm.slug}`}
      className="rounded-md border border-stone-200 bg-white p-4"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-[10px] font-underground-sc tracking-wider text-stone-400">
            {firm.sector}
          </div>
          <div className="font-editorial text-lg text-[#002147]">
            {firm.firm_name}
          </div>
          {firm.freshness?.as_of && (() => {
            const label = vintageLabelFor(firm.freshness.as_of);
            const stale = firm.freshness.is_stale;
            return (
              <span
                data-testid={`compare-firm-${firm.slug}-vintage-chip`}
                title={
                  stale
                    ? `These numbers were verified in ${label} and are overdue for our 90-day refresh — check the sources on the firm's page.`
                    : `Verified in ${label}. Next refresh scheduled ${firm.freshness.next_refresh_at || "shortly"}.`
                }
                className={
                  "mt-1.5 inline-flex items-center gap-1 text-[9px] font-underground-sc tracking-wider rounded-full border px-1.5 py-0.5 " +
                  (stale
                    ? "border-amber-400 bg-amber-50 text-amber-900"
                    : "border-emerald-400 bg-white text-emerald-800")
                }
              >
                <ShieldCheck className="h-2 w-2" aria-hidden />
                {stale ? `Refresh due · Verified ${label}` : `Verified ${label}`}
              </span>
            );
          })()}
        </div>
        <Link
          to={`/employers/${firm.slug}`}
          className="text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
        >
          View →
        </Link>
      </div>
      <div className="mt-3 space-y-2">
        {(firm.flagship_by_location || []).map((fbl, i) => (
          <div
            key={i}
            className="text-[12px] flex items-baseline justify-between gap-2"
          >
            <span className="text-stone-500">{fbl.location_label}</span>
            <span className="font-mono text-stone-800">
              {formatFrom(fbl.base_annual, fbl.currency)}
              {fbl.total_max_annual != null && (
                <>
                  {" → "}
                  {formatFrom(fbl.total_max_annual, fbl.currency)}
                </>
              )}
            </span>
          </div>
        ))}
      </div>
      {firm.freshness?.next_refresh_at && (
        <div className="mt-2 text-[10px] font-mono text-stone-400">
          Next refresh {firm.freshness.next_refresh_at}
        </div>
      )}
    </div>
  );
}

/**
 * FeederOverlapSection — for each firm slug in the compare set, show
 * the top feeder (university × subject) rows sourced from every
 * degree's `outcomes.destinations.top_employers` list.
 *
 * Answers the family's real question ("which uni actually gets me
 * to Google?") by putting the sourced % of cohort right next to the
 * pay bar. Rows without any feeder matches render as a soft empty
 * state so the compare column-count stays stable.
 */
function FeederOverlapSection({ slugs, feeders }) {
  const anyLoaded = slugs.some((s) => feeders[s]);
  return (
    <div
      data-testid="compare-feeder-overlap"
      className="mt-10 rounded-lg border border-[#4C1D95]/20 bg-[#4C1D95]/[0.03] p-5"
    >
      <div className="flex items-baseline gap-2">
        <GraduationCap className="h-4 w-4 text-[#4C1D95]" />
        <div className="label-uppercase text-[#4C1D95] font-semibold">
          Top feeder universities
        </div>
      </div>
      <p className="mt-1 text-[13px] text-stone-600 max-w-2xl leading-relaxed">
        Which universities actually place graduates into these firms —
        sourced from HESA / HEA / TUM Career Service destinations
        surveys attached to each degree in our editorial catalog.
      </p>

      {!anyLoaded && (
        <div className="mt-4 text-[12px] text-stone-500">Loading feeder data…</div>
      )}

      <div className={`mt-4 grid grid-cols-1 ${slugs.length > 1 ? "md:grid-cols-2" : ""} gap-4`}>
        {slugs.map((s) => {
          const f = feeders[s];
          if (!f) {
            return (
              <div
                key={s}
                data-testid={`feeder-overlap-${s}-loading`}
                className="rounded-md border border-dashed border-stone-300 bg-white/60 p-3 text-[12px] text-stone-500"
              >
                <div className="font-underground-sc tracking-wider text-[10px] text-stone-400">{s}</div>
                <div className="mt-1">Loading…</div>
              </div>
            );
          }
          const rows = f.feeder_unis || [];
          return (
            <div
              key={s}
              data-testid={`feeder-overlap-${s}`}
              className="rounded-md border border-stone-200 bg-white p-4"
            >
              <div className="flex items-baseline justify-between">
                <div className="font-editorial text-[#002147] text-lg">
                  {f.firm_name}
                </div>
                <Link
                  to={`/employers/${s}`}
                  className="text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
                >
                  Full page →
                </Link>
              </div>

              {rows.length === 0 ? (
                <div
                  data-testid={`feeder-overlap-${s}-empty`}
                  className="mt-3 text-[12px] text-stone-500"
                >
                  No sourced destinations yet for {f.firm_name}. As more UK/NL/IE/DE
                  degrees get editorial destinations, this list fills in.
                </div>
              ) : (
                <ul className="mt-3 space-y-2">
                  {rows.slice(0, 5).map((r, i) => (
                    <li
                      key={i}
                      data-testid={`feeder-overlap-${s}-row-${i}`}
                      className="flex items-baseline justify-between gap-3 text-[12.5px]"
                    >
                      <div className="min-w-0">
                        <div className="text-stone-800 truncate">
                          {r.university_name}
                          <span className="text-stone-400 text-[11px] ml-1">
                            · {r.country}
                          </span>
                        </div>
                        <div className="text-[11px] text-stone-500 truncate">
                          {r.subject_area}
                          {r.role_hint && (
                            <span className="text-stone-400"> · {r.role_hint}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="font-mono font-semibold text-[#4C1D95]">
                          {r.pct_of_cohort != null
                            ? `${r.pct_of_cohort.toFixed(1)}%`
                            : "—"}
                        </div>
                        <div className="text-[10px] font-mono text-stone-400">
                          of cohort
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {rows.length > 0 && rows[0].source && (
                <div
                  data-testid={`feeder-overlap-${s}-source`}
                  className="mt-3 pt-2 border-t border-stone-100 text-[10px] font-mono text-stone-400 leading-tight"
                >
                  Sourced from {rows[0].source}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
